import React, { useCallback, useEffect } from 'react'
import { useNavigation } from '@react-navigation/native'
import { useBusiness } from '@vatom/sdk/react'
import { PressableOpacity, Text, theme } from '@vatom/wombo'
import { observer } from 'mobx-react-lite'
import { Box, HStack, VStack } from 'native-base'

import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Title } from '../../components/Title'
import { useBusinessThemeConfig } from '../../hooks/useBusinessTheme'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useLoyaltyCoins } from '../../hooks/useLoyalty'
import { AppRoutes } from '../../navigators'
import { TransactionList } from '../CoinDetail/partials/TransactionHistory/TransactionList'
import { CoinStackRoutes } from '../CoinNavigation/CoinNavigation'
import { useCoinStore } from '../CoinNavigation/useCoinStore'

import { CoinsList } from './partials/CoinsList'

const CoinsScreen = observer(() => {
  const navigation = useNavigation()

  const isDesktop = useIsDesktop()

  const { isLoading, data: coinsData, refetch, isRefetching } = useLoyaltyCoins()

  const onPressCoin = useCallback(
    (coinId: string): void => {
      navigation.navigate(AppRoutes.coin, {
        coinId
      })
    },
    [navigation]
  )

  return isDesktop ? (
    <DesktopLayout coinsData={coinsData} isLoading={isLoading} />
  ) : (
    <CoinsList
      data={coinsData}
      isLoading={isLoading}
      onPressCoin={onPressCoin}
      containerProps={{
        marginX: 4
      }}
      onRefresh={() => refetch()}
      isRefreshing={isRefetching}
    />
  )
})

type ActionButtonProps = {
  businessId: string
  onPress: () => void
}
const ActionButton = ({ businessId, onPress }: ActionButtonProps) => {
  const { data: business } = useBusiness({ businessId })
  const { brandTheme, pageTheme } = useBusinessThemeConfig({
    pageConfig: business?.pageConfig,
    brandConfig: business?.brandConfig
  })

  const styles = {
    primary: brandTheme?.primary ?? pageTheme.colorHeader,
    primaryText: brandTheme?.primaryText ?? pageTheme.primaryText
  }

  return (
    <PressableOpacity
      onPress={onPress}
      paddingX={5}
      borderRadius={3}
      backgroundColor={styles.primary}
      height={10}
      display={'flex'}
      justifyContent={'center'}
    >
      <Text color={styles.primaryText} tx="coinsScreen.sendCoins" />
    </PressableOpacity>
  )
}

const DesktopLayout = ({
  coinsData,
  isLoading
}: {
  coinsData: ReturnType<typeof useLoyaltyCoins>['data']
  isLoading: boolean
}) => {
  const navigation = useNavigation()
  const { coinId, setCoinId, setBusinessId, businessId } = useCoinStore()
  const setCoinIdAndBusinessId = useCallback(
    (coinId: string, businessId: string) => {
      setCoinId(coinId)
      setBusinessId(businessId)
    },
    [setBusinessId, setCoinId]
  )

  useEffect(() => {
    // Set the first coin on the list
    if (!coinId && coinsData && !isLoading) {
      const coin = coinsData[0]
      setCoinIdAndBusinessId(coin?.id, coin?.businessId)
    }
  }, [coinId, coinsData, isLoading, setCoinIdAndBusinessId])

  const onPressCoin = useCallback(
    (coinId: string) => {
      const coin = coinsData?.find(c => c.id === coinId)
      if (coin) setCoinIdAndBusinessId(coin.id, coin.businessId)
    },
    [setCoinIdAndBusinessId, coinsData]
  )

  const onPressSend = useCallback(() => {
    const coinId = useCoinStore.getState()?.coinId
    navigation.navigate(AppRoutes.coin, {
      coinId: coinId,
      screen: CoinStackRoutes.CoinTransfer
    })
  }, [navigation])

  return (
    <ScreenWrapper screenBackgroundColor={theme.colors.grayCool[50]}>
      <HStack
        backgroundColor={theme.colors.white}
        borderRadius={8}
        borderWidth={1}
        borderColor={theme.colors.grayCool[100]}
        marginTop={8}
        marginBottom={8}
        padding={8}
      >
        <VStack flex={1} maxWidth={376}>
          <Title preset="h3" variant="bold" marginTop={3} tx="coinsScreen.myCoins" />
          <Box marginTop={8}>
            <CoinsList data={coinsData} isLoading={isLoading} onPressCoin={onPressCoin} />
          </Box>
        </VStack>
        <VStack flex={1} marginLeft={9}>
          <HStack>
            <Title
              preset="h3"
              variant="bold"
              flex={1}
              marginTop={3}
              tx="common.transactionHistory"
            />
            {businessId && <ActionButton onPress={onPressSend} businessId={businessId} />}
          </HStack>
          <Box marginTop={8}>
            {businessId ? (
              <TransactionList businessId={businessId} />
            ) : (
              <Box>
                <Text tx="coinsScreen.noSelected" />
              </Box>
            )}
          </Box>
        </VStack>
      </HStack>
    </ScreenWrapper>
  )
}

export default CoinsScreen
